import React, { useState, useRef, useEffect, useContext } from 'react';
import { 
  Search, 
  ChevronDown, 
  User, 
  Settings, 
  LogOut, 
  Lock, 
  BookOpen, 
  FileText, 
  Sparkles, 
  DollarSign,
  Menu,
  X,
  Home,
  MessageSquare,
  Users,
  Bell,
  Shield,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProfilePicture } from '../../contexts/ProfilePictureContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useTenantData } from '../../contexts/TenantDataContext';
import { buildPublicBookingUrl } from '../../utils/publicBookingUrl';
import { usePermissions } from '../../hooks/usePermissions';
import { AdminContext } from '../../contexts/AdminContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus';
import { useNotifications } from '../../contexts/NotificationContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NotificationDropdown from '../shared/NotificationDropdown';
import AddCardNavButton from '../billing/AddCardNavButton';
import ChangePasswordModal from '../profile/ChangePasswordModal';
import AccountSwitcher from '../shared/AccountSwitcher';
import AccountSwitcherNavButton from '../shared/AccountSwitcherNavButton';
import { ContactService } from '../../api/services/ContactService';
import type { Routemize_DTOs_ContactDTO } from '../../api/models/Routemize_DTOs_ContactDTO';

// Tooltip component for hover descriptions
const Tooltip: React.FC<{
  children: React.ReactNode;
  content: string;
  subtext?: string;
  hint?: React.ReactNode;
}> = ({
  children,
  content,
  subtext,
  hint,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  
  return (
    <div 
      ref={triggerRef}
      className="relative inline-flex w-fit"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div 
          className="
            absolute inset-x-0 top-full mt-1.5
            z-[100] flex justify-center
            pointer-events-none
          "
        >
          <div
            className="
              px-2.5 py-1.5
              bg-ink-900 text-white text-11 font-medium
              rounded-md shadow-lg
              animate-in fade-in zoom-in-95 duration-100
              whitespace-nowrap
              relative
            "
          >
            {content}
            {subtext && (
              <div className="text-ink-400 text-2xs">{subtext}</div>
            )}
            {hint && (
              <div className="mt-1 pt-1 border-t border-white/10 flex items-center gap-1 text-blue-300 text-2xs font-medium">
                <Settings className="h-2.5 w-2.5" />
                {hint}
              </div>
            )}
            {/* Arrow */}
            <div 
              className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-ink-900 rotate-45"
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface NavbarProps {
  className?: string;
  onMobileMenuOpen?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ className, onMobileMenuOpen }) => {
  const { logout, isAuthenticated } = useAuth();
  const { config } = useAppConfig();
  const { defaultBookingFormLink, companySettings } = useTenantData();
  const { unreadCount, menuCounts } = useNotifications();
  const location = useLocation();
  
  // Safely get onboarding context
  let onboardingContext;
  try {
    onboardingContext = useOnboarding();
  } catch {
    onboardingContext = null;
  }
  
  // Safely get admin context
  const adminContext = useContext(AdminContext);
  const isHostAdmin = adminContext?.isHostAdmin ?? false;

  // Role-based gating
  const { roleType, canEditCompanySettings, canViewBilling } = usePermissions();
  const isSchedulingAssistant = roleType === 'booking_admin';
  const isTeamMember = roleType === 'user';
  // Guided tutorial is only available to Super Users (hidden for Scheduling Assistants and Team Members)
  // and is hidden once onboarding has been completed at least once.
  const { needsOnboarding } = useOnboardingStatus();
  const canStartGuidedTutorial = !isSchedulingAssistant && !isTeamMember && needsOnboarding;

  // Get profile picture from context
  const { getImageUrl } = useProfilePicture();
  const iconUrl = getImageUrl('icon');
  const thumbUrl = getImageUrl('thumb');

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<Routemize_DTOs_ContactDTO[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Page navigation is handled by the global CommandPalette (Cmd+K); the navbar
  // search is scoped to contacts only to avoid duplicating the palette.
  const showDropdown = searchFocused && searchQuery.length >= 1 && (searchResults.length > 0 || isSearching);
  const totalResults = searchResults.length;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced contact search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        // searchText, status, tagIds, source, isFavorite, lastContactDateFrom, lastContactDateTo, sorting, skipCount, maxResultCount
        const response = await ContactService.getApiAppContactGetList(
          searchQuery,
          undefined, undefined, undefined, undefined, undefined, undefined,
          undefined, 0, 6,
        );
        const items = (response as { items?: Routemize_DTOs_ContactDTO[]; totalCount?: number })?.items ?? [];
        setSearchResults(items);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedResultIndex(-1);
  }, [searchResults, searchQuery]);

  const handleSelectResult = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setSearchFocused(false);
    searchInputRef.current?.blur();
  };

  // Cmd/Ctrl+K is owned globally by the CommandPalette. Navbar search keeps
  // only the local Escape-to-clear behaviour so it doesn't compete.
  useEffect(() => {
    if (!searchFocused) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        searchInputRef.current?.blur();
        setSearchQuery('');
        setSearchFocused(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchFocused]);

  const handleLogout = async () => {
    await logout();
  };

  const getUserDisplayName = () => {
    const currentUser = config?.currentUser;
    if (!currentUser) return '';

    const name = currentUser.name?.trim();
    const surName = currentUser.surName?.trim();
    if (name && surName) {
      return `${name} ${surName}`;
    }
    if (name) return name;
    return currentUser.email?.split('@')[0] || '';
  };

  const getUserInitials = () => {
    const currentUser = config?.currentUser;
    if (!currentUser) return '';

    const name = currentUser.name?.trim();
    const surName = currentUser.surName?.trim();
    if (name && surName) {
      return (name[0] + surName[0]).toUpperCase();
    }
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    }
    return currentUser.email?.[0]?.toUpperCase() || '';
  };

  const getUserEmail = () => {
    return config?.currentUser?.email || '';
  };

  return (
    <>
      <nav className={`
        relative z-40
        bg-surface/95 backdrop-blur-xl
        border-b border-ink-100
        h-14 md:h-[52px] px-3 md:px-5
        flex items-center justify-between gap-3
        shadow-[0_1px_3px_rgba(0,0,0,0.02)]
        ${className}
      `}>
        {/* Left Section: Mobile menu + Logo (mobile only) */}
        <div className="flex items-center gap-2.5 md:hidden">
          {/* Mobile Menu Toggle */}
          <button
            onClick={onMobileMenuOpen}
            className="
              p-2 -ml-1.5 
              text-ink-500 hover:text-ink-900 
              hover:bg-ink-100/80 active:bg-ink-200/60
              rounded-lg transition-all duration-150
            "
            aria-label="Open menu"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>
          
          {/* Mobile Logo */}
          <Link to="/bookings" className="flex items-center">
            <img 
              src="/106780_logo_falt_RK_DK_05.png" 
              alt="Routemize" 
              className="h-8 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Desktop: Empty left spacer (logo is in sidebar) */}
        <div className="hidden md:block w-2" />

        {/* Center: Search Bar */}
        {isAuthenticated && (
          <div className="hidden md:block flex-1 max-w-md" ref={searchContainerRef}>
            <div className={`
              relative transition-all duration-200 ease-out
              ${searchFocused ? 'scale-[1.005]' : ''}
            `}>
              <Search className={`
                absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10
                transition-colors duration-150
                ${searchFocused ? 'text-blue-500' : 'text-ink-400'}
              `} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search contacts..."
                aria-label="Search contacts"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onKeyDown={(e) => {
                  if (!showDropdown) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedResultIndex(i => Math.min(i + 1, totalResults - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedResultIndex(i => Math.max(i - 1, -1));
                  } else if (e.key === 'Enter' && selectedResultIndex >= 0) {
                    e.preventDefault();
                    const contact = searchResults[selectedResultIndex];
                    if (contact?.id) handleSelectResult(`/contacts/${contact.id}`);
                  }
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                className={`
                  w-full pl-9 pr-4 py-2
                  text-sm text-ink-900 placeholder-ink-400
                  bg-ink-50/70
                  border rounded-lg
                  transition-all duration-200 ease-out
                  ${searchFocused 
                    ? 'border-blue-200 ring-2 ring-blue-100 bg-surface shadow-sm' 
                    : 'border-ink-200/80 hover:border-ink-300 hover:bg-ink-100/70'
                  }
                  focus:outline-none
                `}
              />
              {/* Cmd+K opens the global command palette, not this search */}

              {/* Search Results Dropdown */}
              {showDropdown && (
                <div className="
                  absolute top-full left-0 right-0 mt-1.5
                  bg-surface border border-ink-200 rounded-xl
                  shadow-lg shadow-ink-200/60
                  overflow-hidden z-50
                  animate-in fade-in slide-in-from-top-1 duration-150
                ">
                  <div className="max-h-[360px] overflow-y-auto">
                    {/* Contact Results */}
                    {(searchResults.length > 0 || isSearching) && (
                      <div className="p-1.5">
                        <div className="px-2.5 py-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">
                          Contacts
                        </div>
                        {isSearching && searchResults.length === 0 ? (
                          <div className="px-2.5 py-3 text-sm text-ink-400 flex items-center gap-2">
                            <div className="h-4 w-4 border-2 border-ink-300 border-t-blue-500 rounded-full animate-spin" />
                            Searching...
                          </div>
                        ) : (
                          searchResults.map((contact, index) => {
                            const displayName = contact.displayName || contact.fullName || 
                              [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unnamed';
                            const isSelected = index === selectedResultIndex;
                            return (
                              <button
                                key={contact.id}
                                className={`
                                  w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors
                                  ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-ink-700 hover:bg-ink-50'}
                                `}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => contact.id && handleSelectResult(`/contacts/${contact.id}`)}
                                onMouseEnter={() => setSelectedResultIndex(index)}
                              >
                                <div className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-11 font-medium flex-shrink-0
                                  ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-ink-100 text-ink-600'}
                                `}>
                                  {(contact.firstName?.[0] || '').toUpperCase()}{(contact.lastName?.[0] || '').toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{displayName}</div>
                                  <div className="text-xs text-ink-500 truncate">
                                    {contact.email || contact.phoneNumber || contact.businessName || ''}
                                  </div>
                                </div>
                                <Users className="h-3.5 w-3.5 text-ink-300 flex-shrink-0" />
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* No results state */}
                    {!isSearching && searchResults.length === 0 && searchQuery.length >= 2 && (
                      <div className="px-4 py-6 text-center">
                        <div className="text-sm text-ink-500">No contacts match "{searchQuery}"</div>
                        <div className="text-xs text-ink-400 mt-1">
                          Press <kbd className="px-1 py-0.5 bg-surface border border-ink-200 rounded text-ink-500">⌘K</kbd> to jump to a page
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer hint */}
                  {totalResults > 0 && (
                    <div className="px-3 py-2 border-t border-ink-100 bg-ink-50/50 flex items-center gap-3 text-2xs text-ink-400">
                      <span className="flex items-center gap-1">
                        <kbd className="px-1 py-0.5 bg-surface border border-ink-200 rounded text-ink-500">↑↓</kbd>
                        navigate
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-1 py-0.5 bg-surface border border-ink-200 rounded text-ink-500">↵</kbd>
                        select
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-1 py-0.5 bg-surface border border-ink-200 rounded text-ink-500">esc</kbd>
                        close
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Section: Actions + User */}
        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Desktop Action Buttons - Customer Entry Tools */}
          {isAuthenticated && (
            <>
              {/* Divider before customer tools */}
              <div className="hidden sm:flex items-center mr-1">
                <div className="h-6 w-px bg-ink-200" />
              </div>
              
              {/* Customer Entry Tools Label (visible on larger screens) */}
              <div className="hidden xl:flex items-center mr-1">
                <span className="text-2xs uppercase tracking-wider font-semibold text-ink-400">
                  Customer Entry
                </span>
              </div>
              
              {/* Routemize Flow - Opens in New Tab */}
              <Tooltip 
                content="Smart booking for customer"
                subtext="Opens in new tab"
                hint="Change default form in Settings → Forms"
              >
                <button
                  onClick={() => window.open(
                    defaultBookingFormLink
                      ? buildPublicBookingUrl(defaultBookingFormLink, companySettings?.subdomain)
                      : '/booking-forms',
                    '_blank'
                  )}
                  className="
                    hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2
                    text-13 font-medium text-white
                    bg-gradient-to-r from-blue-600 to-blue-500
                    hover:from-blue-500 hover:to-blue-400
                    rounded-lg transition-all duration-200
                    shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/25
                    group/routemize
                    focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-1
                  "
                  aria-label="Smart booking for customer - opens in new tab"
                >
                  <Sparkles className="h-3.5 w-3.5 transition-transform group-hover/routemize:scale-110 group-hover/routemize:rotate-12" />
                  <span className="hidden md:inline">Routemize</span>
                  <ExternalLink className="h-3 w-3 text-white/60 group-hover/routemize:text-white/80 transition-colors" />
                </button>
              </Tooltip>
              
              {/* Divider after customer tools */}
              <div className="hidden sm:flex items-center ml-1 mr-1">
                <div className="h-6 w-px bg-ink-200" />
              </div>
              
              {/* Persistent add-a-card prompt — renders nothing once a card is on file */}
              <AddCardNavButton />

              {/* Tutorial Button - hidden for scheduling assistants and team members */}
              {onboardingContext && canStartGuidedTutorial && (
                <Tooltip content="Start guided tutorial">
                  <button
                    onClick={() => onboardingContext.openModal()}
                    className="
                      hidden md:flex items-center justify-center p-2
                      text-ink-500 hover:text-blue-600
                      hover:bg-blue-50
                      rounded-lg transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    "
                    aria-label="Launch Tutorial"
                  >
                    <BookOpen className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}
              
              {/* Notifications */}
              <NotificationDropdown />

              {/* Named account button — renders nothing unless this person owns more than one */}
              <AccountSwitcherNavButton />
            </>
          )}

          {/* User Profile Dropdown */}
          {isAuthenticated && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className={`
                  flex items-center gap-1.5 p-1 pr-1.5 md:pr-2
                  rounded-lg transition-all duration-150 ease-out
                  ${showUserDropdown 
                    ? 'bg-ink-100' 
                    : 'hover:bg-ink-50/80'
                  }
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20
                `}
              >
                {/* Avatar */}
                <div className="
                  h-7 w-7 rounded-md overflow-hidden
                  bg-gradient-to-br from-blue-500 to-blue-600
                  flex items-center justify-center
                  shadow-sm
                ">
                  {iconUrl ? (
                    <img src={iconUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-11 font-semibold text-white leading-none">
                      {getUserInitials()}
                    </span>
                  )}
                </div>
                
                {/* Name & Email (Desktop) */}
                <div className="hidden md:block text-left max-w-[120px]">
                  <div className="text-13 font-medium text-ink-900 truncate leading-tight">
                    {getUserDisplayName()}
                  </div>
                </div>
                
                <ChevronDown className={`
                  hidden md:block h-3.5 w-3.5 text-ink-400
                  transition-transform duration-150
                  ${showUserDropdown ? 'rotate-180' : ''}
                `} />
              </button>

              {/* Dropdown Menu */}
              {showUserDropdown && (
                <div className="
                  absolute right-0 mt-1.5 w-60
                  bg-surface
                  rounded-lg shadow-lg shadow-ink-200/50
                  border border-ink-200/80
                  overflow-hidden
                  z-50
                  animate-in fade-in slide-in-from-top-1 duration-150
                ">
                  {/* User Header */}
                  <div className="px-3 py-3 bg-ink-50/50 border-b border-ink-100">
                    <div className="flex items-center gap-2.5">
                      <div className="
                        h-9 w-9 rounded-lg overflow-hidden
                        bg-gradient-to-br from-blue-500 to-blue-600
                        flex items-center justify-center
                        shadow-md shadow-blue-500/20
                      ">
                        {thumbUrl ? (
                          <img src={thumbUrl} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-white">
                            {getUserInitials()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink-900 truncate">
                          {getUserDisplayName()}
                        </div>
                        <div className="text-xs text-ink-500 truncate">
                          {getUserEmail()}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="p-1.5">
                    <Link
                      to="/profile"
                      className="flex items-center gap-2.5 px-2.5 py-2 text-13 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      <User className="h-4 w-4 text-ink-400" />
                      Profile
                    </Link>
                    
                    {canEditCompanySettings && (
                      <Link
                        to="/settings"
                        className="flex items-center gap-2.5 px-2.5 py-2 text-13 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <Settings className="h-4 w-4 text-ink-400" />
                        Settings
                      </Link>
                    )}

                    {canViewBilling && (
                      <Link
                        to="/billing"
                        className="flex items-center gap-2.5 px-2.5 py-2 text-13 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <DollarSign className="h-4 w-4 text-ink-400" />
                        Billing
                      </Link>
                    )}

                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        setShowChangePasswordModal(true);
                      }}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-13 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                    >
                      <Lock className="h-4 w-4 text-ink-400" />
                      Change Password
                    </button>

                    {isHostAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-2.5 px-2.5 py-2 text-13 text-ink-700 hover:bg-ink-50 rounded-md transition-colors"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <Shield className="h-4 w-4 text-ink-400" />
                        Administration
                      </Link>
                    )}
                  </div>

                  {/* Accounts this person owns — renders nothing when there is only one */}
                  <AccountSwitcher onSwitchStarted={() => setShowUserDropdown(false)} />

                  {/* Logout */}
                  <div className="p-1.5 border-t border-ink-100">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 text-13 text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  );
};

export default Navbar;
